import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, Input, Segmented, Select, TextArea, useToast } from "@agentsaas/ui";
import { pickLocalized, SUPPORTED_LOCALES, type Listing, type Locale, type TemplateId } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime, formatMicroUsd, formatNumber } from "../app/format";
import { qk, useCredentials, useProjects, useTemplates } from "../data/queries";
import { bk, useWallet } from "../data/billing";
import { mkt, useListing, useListings, useListingsById, useMyListings, useRentals, type ListingQuery } from "../data/market";
import { Loading } from "../layout/Loading";
import { SubFrame, TenantCrumb } from "../layout/SubFrame";
import { LISTING_TONE, parseUsd, priceParams, rentalPrice, starFill, type Pricing } from "./model";

/**
 * /t/:tid/market[/l/:lid | /mine | /rentals]：agent 市场。
 * - 浏览：搜索、按角色筛选、排序；卡片显示标题（按界面语言）、评分、租用次数、价格
 * - 详情：多语言标题 / 描述（可切换查看其他语言）、模板信息；租用（选项目、凭据、名字、天数，确认总价与余额）；评价（租用过才能评）
 * - 我上架的：上架 / 编辑（契约没有编辑接口：同一模板再次提交 = 更新并重新审核）
 * - 我的租用
 */

type Section = "browse" | "mine" | "rentals";
type Sort = "popular" | "rating" | "newest";
const SORTS: Sort[] = ["popular", "rating", "newest"];

export function MarketPage({ section }: { section: Section }) {
  const { tid = "" } = useParams();
  const { t } = useT();
  const items = [
    { id: "browse", to: `/t/${tid}/market`, label: t("market.nav.browse"), icon: "store" as const },
    { id: "mine", to: `/t/${tid}/market/mine`, label: t("market.nav.mine"), icon: "upload" as const },
    { id: "rentals", to: `/t/${tid}/market/rentals`, label: t("market.nav.rentals"), icon: "users" as const },
  ];
  return (
    <>
      <TenantCrumb tid={tid} section={t("market.title")} />
      <SubFrame label={t("market.title")} items={items} active={section}>
        {section === "browse" ? <Browse tid={tid} /> : section === "mine" ? <MyListings tid={tid} /> : <MyRentals tid={tid} />}
      </SubFrame>
    </>
  );
}

export function Stars({ rating, count }: { rating: number | null; count?: number }) {
  const { t, locale } = useT();
  const fill = starFill(rating);
  const label = rating === null ? t("market.no_rating") : t("market.rating", { rating: formatNumber(rating, locale, { maximumFractionDigits: 1 }) });
  return (
    <span className="stars" role="img" aria-label={label} title={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon key={i} name="star" size={14} className={fill >= i ? "stars__on" : fill >= i - 0.5 ? "stars__half" : "stars__off"} />
      ))}
      {rating !== null ? <span className="small num">{formatNumber(rating, locale, { maximumFractionDigits: 1 })}</span> : null}
      {count !== undefined ? <span className="small muted">{t("market.rentals_count", { count })}</span> : null}
    </span>
  );
}

function PriceText({ pricing }: { pricing: Pricing }) {
  const { t, locale } = useT();
  const p = priceParams(pricing, locale);
  return <span className="price">{t(p.key, { price: p.price })}</span>;
}

// ───────────── 浏览 ─────────────

function Browse({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") ?? "";
  const role = sp.get("role") ?? "";
  const sort = (SORTS.includes(sp.get("sort") as Sort) ? sp.get("sort") : "popular") as Sort;
  const [text, setText] = useState(q);
  const set = (k: string, v: string) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  // 搜索词防抖写回 URL
  useEffect(() => {
    if (text === q) return;
    const h = setTimeout(() => set("q", text.trim()), 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  const query: ListingQuery = { sort, ...(q ? { q } : {}), ...(role ? { role } : {}) };
  const listings = useListings(query);
  // 管理员会拿到待审核的商品（审核队列用）；市场只展示已发布的
  const items = (listings.data ?? []).filter((l) => l.status === "published");
  const allRoles = useListings({ sort: "popular" });
  const roles = useMemo(() => [...new Set((allRoles.data ?? []).filter((l) => l.status === "published").map((l) => l.role))].sort(), [allRoles.data]);

  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("market.nav.browse")}</h1>
      </div>
      <div className="filters row" role="search">
        <Input type="search" aria-label={t("market.search")} placeholder={t("market.search")} value={text} onChange={(e) => setText(e.target.value)} data-testid="market-search" />
        <Select aria-label={t("market.role")} value={role} onChange={(e) => set("role", e.target.value)} data-testid="market-role">
          <option value="">{t("market.all_roles")}</option>
          {roles.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
        <Segmented<Sort> label={t("market.sort")} value={sort} onChange={(v) => set("sort", v)} options={SORTS.map((s) => ({ value: s, label: t(`market.sort.${s}`) }))} />
      </div>
      {listings.isPending ? (
        <Loading />
      ) : items.length ? (
        <ul className="market-grid" data-testid="market-list">
          {items.map((l) => (
            <li key={l.id}>
              <Link to={`/t/${tid}/market/l/${l.id}`} className="card market-card" data-testid="market-card">
                <div className="row">
                  <strong className="grow market-card__title">{pickLocalized(l.title, locale)}</strong>
                  <Badge tone="accent">{l.role}</Badge>
                </div>
                <p className="small muted market-card__desc">{pickLocalized(l.description, locale)}</p>
                <div className="row market-card__foot">
                  <Stars rating={l.rating} count={l.rentals} />
                  <span className="grow" />
                  <PriceText pricing={l.pricing} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="store" title={t("market.empty")} />
      )}
    </div>
  );
}

// ───────────── 详情 ─────────────

export function ListingPage() {
  const { tid = "", lid = "" } = useParams();
  const { t, locale } = useT();
  const listing = useListing(lid);
  const rentals = useRentals(tid);
  const [lang, setLang] = useState<Locale | null>(null);
  const [renting, setRenting] = useState(false);
  const items = [
    { id: "browse", to: `/t/${tid}/market`, label: t("market.nav.browse"), icon: "store" as const },
    { id: "mine", to: `/t/${tid}/market/mine`, label: t("market.nav.mine"), icon: "upload" as const },
    { id: "rentals", to: `/t/${tid}/market/rentals`, label: t("market.nav.rentals"), icon: "users" as const },
  ];
  const l = listing.data;
  const langs = l ? SUPPORTED_LOCALES.filter((x) => l.title[x] || l.description[x]) : [];
  const shown: Locale = lang && langs.includes(lang) ? lang : langs.includes(locale) ? locale : (langs[0] ?? locale);
  const rented = !!l && (rentals.data ?? []).some((r) => r.listingId === l.id);
  return (
    <>
      <TenantCrumb tid={tid} section={t("market.title")} />
      <SubFrame label={t("market.title")} items={items} active="browse" wide={false}>
        {listing.isPending ? (
          <Loading />
        ) : !l ? (
          <EmptyState icon="store" title={t("error.market.listing_unavailable")} />
        ) : (
          <article className="stack listing" data-testid="listing-detail">
            <Link to={`/t/${tid}/market`} className="small row">
              <Icon name="chevronLeft" size={16} />
              {t("market.back")}
            </Link>
            <div className="page__head">
              <div className="stack" style={{ gap: 4 }}>
                <h1 className="page__title" lang={shown} data-testid="listing-title">
                  {pickLocalized(l.title, shown)}
                </h1>
                <div className="row">
                  <Badge tone="accent">{l.role}</Badge>
                  {l.status !== "published" ? <Badge tone={LISTING_TONE[l.status]}>{t(`market.status.${l.status}`)}</Badge> : null}
                  <Stars rating={l.rating} count={l.rentals} />
                </div>
              </div>
              <div className="stack listing__buy">
                <strong className="listing__price">
                  <PriceText pricing={l.pricing} />
                </strong>
                <Button variant="primary" icon="plus" onClick={() => setRenting(true)} disabled={l.status !== "published"} data-testid="rent">
                  {t("market.rent")}
                </Button>
              </div>
            </div>
            {langs.length > 1 ? (
              <Segmented<Locale> label={t("market.language")} value={shown} onChange={setLang} options={langs.map((x) => ({ value: x, label: t(`locale.${x}`) }))} />
            ) : null}
            <p className="listing__desc" lang={shown} data-testid="listing-desc">
              {pickLocalized(l.description, shown)}
            </p>
            <section className="card stack">
              <h2 className="section-title">{t("market.template")}</h2>
              <dl className="kv small">
                <dt>{t("market.template_name")}</dt>
                <dd>{pickLocalized(l.template.name, locale)}</dd>
                <dt>{t("market.role")}</dt>
                <dd>{l.template.role}</dd>
                <dt>{t("market.model")}</dt>
                <dd className="mono">{l.template.defaultModel}</dd>
                <dt>{t("market.scopes")}</dt>
                <dd>{l.template.mcpScopes.join(", ")}</dd>
                <dt>{t("market.version")}</dt>
                <dd>{l.template.version}</dd>
              </dl>
            </section>
            <ReviewBox listing={l} rented={rented} />
            <RentDialog tid={tid} listing={l} open={renting} onClose={() => setRenting(false)} />
          </article>
        )}
      </SubFrame>
    </>
  );
}

function RentDialog({ tid, listing, open, onClose }: { tid: string; listing: Listing; open: boolean; onClose(): void }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { api } = useServices();
  const projects = useProjects(tid).data ?? [];
  const creds = (useCredentials(tid).data ?? []).filter((c) => c.status !== "revoked");
  const wallet = useWallet(tid);
  const [pid, setPid] = useState("");
  const [cid, setCid] = useState("");
  const [name, setName] = useState("");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(`${listing.role}-1`);
    void qc.invalidateQueries({ queryKey: bk.wallet(tid) });
  }, [open, listing.role, qc, tid]);
  const firstPid = projects[0]?.id;
  const firstCid = creds[0]?.id;
  useEffect(() => {
    if (!pid && firstPid) setPid(firstPid);
  }, [pid, firstPid]);
  useEffect(() => {
    if (!cid && firstCid) setCid(firstCid);
  }, [cid, firstCid]);

  const perDay = listing.pricing.model === "per_day";
  const nDays = Number(days);
  const daysOk = !perDay || (Number.isInteger(nDays) && nDays >= 1 && nDays <= 365);
  const total = rentalPrice(listing.pricing, perDay ? nDays || 1 : 1);
  const balance = wallet.data?.balanceMicroUsd;
  const short = balance !== undefined && balance < total;
  const valid = !!pid && !!cid && name.trim().length > 0 && daysOk;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.call("listingRent", { params: { lid: listing.id }, body: { projectId: pid as never, credentialId: cid, name: name.trim(), ...(perDay ? { days: nDays } : {}) } });
      void qc.invalidateQueries({ queryKey: mkt.rentals(tid) });
      void qc.invalidateQueries({ queryKey: mkt.all });
      void qc.invalidateQueries({ queryKey: bk.wallet(tid) });
      void qc.invalidateQueries({ queryKey: qk.agents(r.projectId) });
      toast.show({ message: t("market.rented", { name: name.trim() }), tone: "success" });
      onClose();
      navigate(`/p/${r.projectId}/a/${r.agentId}`);
    } catch (err) {
      setError(errText(err));
      void qc.invalidateQueries({ queryKey: bk.wallet(tid) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("market.rent_title", { title: pickLocalized(listing.title, locale) })}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()} data-testid="rent-confirm">
            {total ? t("market.rent_confirm", { price: formatMicroUsd(total, locale) }) : t("market.rent_free")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        {error ? (
          <p className="form-error" role="alert" data-testid="rent-error">
            {error}
          </p>
        ) : null}
        {!projects.length || !creds.length ? (
          <p className="banner banner--warning" role="note">
            {!projects.length ? t("market.need_project") : t("market.need_credential")}
            <Link to={!projects.length ? `/t/${tid}` : `/t/${tid}/credentials`}>{t("market.go_setup")}</Link>
          </p>
        ) : null}
        <Field label={t("market.project")}>
          {(id) => (
            <Select id={id} value={pid} onChange={(e) => setPid(e.target.value)} data-testid="rent-project">
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("market.credential")} hint={t("market.credential_hint")}>
          {(id, d) => (
            <Select id={id} aria-describedby={d} value={cid} onChange={(e) => setCid(e.target.value)} data-testid="rent-credential">
              {creds.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="form-grid">
          <Field label={t("market.agent_name")}>{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={64} data-testid="rent-name" />}</Field>
          {perDay ? (
            <Field label={t("market.days")} error={daysOk ? undefined : t("market.days_invalid")}>
              {(id) => <Input id={id} type="number" inputMode="numeric" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} data-testid="rent-days" />}
            </Field>
          ) : null}
        </div>
        <dl className="kv rent-summary" data-testid="rent-summary">
          <dt>{t("market.pricing")}</dt>
          <dd>
            <PriceText pricing={listing.pricing} />
          </dd>
          <dt>{t("market.total")}</dt>
          <dd>
            <strong className="num" data-testid="rent-total">
              {formatMicroUsd(total, locale)}
            </strong>
          </dd>
          <dt>{t("billing.wallet.balance")}</dt>
          <dd className={short ? "row rent-summary__short" : "row"}>
            <span className="num" data-testid="rent-balance">
              {balance !== undefined ? formatMicroUsd(balance, locale) : "—"}
            </span>
            {short ? (
              <Link to={`/t/${tid}/billing/wallet`} data-testid="rent-topup">
                {t("market.top_up_first")}
              </Link>
            ) : null}
          </dd>
        </dl>
      </form>
    </Dialog>
  );
}

function ReviewBox({ listing, rented }: { listing: Listing; rented: boolean }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!rating) return;
    setBusy(true);
    setError(null);
    try {
      await api.call("listingReview", { params: { lid: listing.id }, body: { rating, comment: comment.trim() } });
      toast.show({ message: t("market.review_thanks"), tone: "success" });
      setComment("");
      void qc.invalidateQueries({ queryKey: mkt.listing(listing.id) });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card stack" aria-labelledby="review-title">
      <h2 id="review-title" className="section-title">
        {t("market.review")}
      </h2>
      {rented ? (
        <form className="stack" onSubmit={submit} data-testid="review-form">
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <fieldset className="star-input">
            <legend className="ui-field__label">{t("market.your_rating")}</legend>
            {[1, 2, 3, 4, 5].map((i) => (
              <label key={i} className={`star-input__opt${rating >= i ? " is-on" : ""}`}>
                <input type="radio" name="rating" value={i} checked={rating === i} onChange={() => setRating(i)} className="ui-sr-only" data-testid={`review-star-${i}`} />
                <Icon name="star" size={24} />
                <span className="ui-sr-only">{t("market.stars", { count: i })}</span>
              </label>
            ))}
          </fieldset>
          <Field label={t("market.comment")}>{(id) => <TextArea id={id} rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />}</Field>
          <Button type="submit" variant="primary" disabled={!rating} loading={busy} data-testid="review-submit">
            {t("market.submit_review")}
          </Button>
        </form>
      ) : (
        <p className="small muted">{t("market.review_after_rent")}</p>
      )}
    </section>
  );
}

// ───────────── 我上架的 ─────────────

function MyListings({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const mine = useMyListings(tid);
  const [editing, setEditing] = useState<Listing | "new" | null>(null);
  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("market.nav.mine")}</h1>
        <Button variant="primary" icon="upload" onClick={() => setEditing("new")} data-testid="listing-new">
          {t("market.publish")}
        </Button>
      </div>
      {mine.isPending ? (
        <Loading />
      ) : mine.data?.length ? (
        <ul className="list card" style={{ padding: 0 }} data-testid="my-listings">
          {mine.data.map((l) => (
            <li key={l.id} className="list-item my-listing" data-testid="my-listing" data-status={l.status}>
              <span className="grow">
                <strong>{pickLocalized(l.title, locale)}</strong>
                <span className="small muted my-listing__meta">
                  <PriceText pricing={l.pricing} />
                  <span className="sep" aria-hidden="true" />
                  {t("market.rentals_count", { count: l.rentals })}
                </span>
              </span>
              <Badge tone={LISTING_TONE[l.status]}>{t(`market.status.${l.status}`)}</Badge>
              <Button size="sm" icon="edit" onClick={() => setEditing(l)} data-testid="listing-edit">
                {t("market.edit")}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="upload" title={t("market.mine_empty")} />
      )}
      <ListingDialog key={editing === null ? "none" : editing === "new" ? "new" : editing.id} tid={tid} editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

const LOCALE_FIELDS: Locale[] = ["zh-CN", "en"];

function ListingDialog({ tid, editing, onClose }: { tid: string; editing: Listing | "new" | null; onClose(): void }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const own = (useTemplates(tid).data ?? []).filter((x) => x.tenantId === tid);
  const cur = editing && editing !== "new" ? editing : null;
  const [tplId, setTplId] = useState<string>(cur?.templateId ?? "");
  const [title, setTitle] = useState<Record<string, string>>({ ...(cur?.title ?? {}) });
  const [desc, setDesc] = useState<Record<string, string>>({ ...(cur?.description ?? {}) });
  const [model, setModel] = useState<Pricing["model"]>(cur?.pricing.model ?? "per_day");
  const [price, setPrice] = useState(cur ? String(cur.pricing.priceMicroUsd / 1_000_000) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tplId && own[0] && !cur) setTplId(own[0].id);
  }, [tplId, own, cur]);

  const micro = model === "free" ? 0 : parseUsd(price);
  const clean = (v: Record<string, string>) => Object.fromEntries(Object.entries(v).map(([k, s]) => [k, s.trim()]).filter(([, s]) => s));
  const hasTitle = Object.keys(clean(title)).length > 0;
  const hasDesc = Object.keys(clean(desc)).length > 0;
  const valid = !!tplId && hasTitle && hasDesc && micro !== null && (model === "free" || micro > 0);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.call("listingCreate", {
        params: { tid },
        body: { templateId: tplId as TemplateId, title: clean(title), description: clean(desc), pricing: { model, priceMicroUsd: micro! } },
      });
      void qc.invalidateQueries({ queryKey: mkt.all });
      toast.show({ message: t("market.submitted"), tone: "success" });
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={editing !== null}
      onClose={onClose}
      title={cur ? t("market.edit_title") : t("market.publish")}
      closeLabel={t("shell.close")}
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()} data-testid="listing-submit">
            {t("market.submit_review_btn")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <p className="small muted">{cur ? t("market.edit_hint") : t("market.publish_hint")}</p>
        {own.length || cur ? (
          <Field label={t("market.template")}>
            {(id) => (
              <Select id={id} value={tplId} onChange={(e) => setTplId(e.target.value)} disabled={!!cur} data-testid="listing-template">
                {own.map((x) => (
                  <option key={x.id} value={x.id}>
                    {pickLocalized(x.name, locale)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <p className="banner banner--warning" role="note">
            {t("market.need_template")} <Link to={`/t/${tid}/templates/new`}>{t("market.create_template")}</Link>
          </p>
        )}
        <div className="form-grid form-grid--2">
          {LOCALE_FIELDS.map((l) => (
            <fieldset key={l} className="listing-lang" lang={l}>
              <legend className="ui-field__label">{t(`locale.${l}`)}</legend>
              <Field label={t("market.field.title")}>
                {(id) => <Input id={id} value={title[l] ?? ""} onChange={(e) => setTitle({ ...title, [l]: e.target.value })} maxLength={120} data-testid={`listing-title-${l}`} />}
              </Field>
              <Field label={t("market.field.description")}>
                {(id) => <TextArea id={id} rows={3} value={desc[l] ?? ""} onChange={(e) => setDesc({ ...desc, [l]: e.target.value })} data-testid={`listing-desc-${l}`} />}
              </Field>
            </fieldset>
          ))}
        </div>
        <p className="small muted">{t("market.lang_hint")}</p>
        <div className="form-grid">
          <Field label={t("market.pricing")}>
            {(id) => (
              <Select id={id} value={model} onChange={(e) => setModel(e.target.value as Pricing["model"])} data-testid="listing-model">
                <option value="per_day">{t("market.model.per_day")}</option>
                <option value="per_rental">{t("market.model.per_rental")}</option>
                <option value="free">{t("market.model.free")}</option>
              </Select>
            )}
          </Field>
          {model !== "free" ? (
            <Field label={t("market.field.price")} error={price.trim() && (micro === null || micro === 0) ? t("market.field.price_invalid") : undefined}>
              {(id) => <Input id={id} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="listing-price" />}
            </Field>
          ) : null}
        </div>
      </form>
    </Dialog>
  );
}

// ───────────── 我的租用 ─────────────

function MyRentals({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const rentals = useRentals(tid);
  const projects = useProjects(tid).data ?? [];
  const listings = useListingsById((rentals.data ?? []).map((r) => r.listingId));
  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("market.nav.rentals")}</h1>
      </div>
      {rentals.isPending ? (
        <Loading />
      ) : rentals.data?.length ? (
        <ul className="list card" style={{ padding: 0 }} data-testid="rental-list">
          {rentals.data.map((r) => {
            const l = listings.get(r.listingId);
            const active = !r.endsAt || new Date(r.endsAt).getTime() > Date.now();
            return (
              <li key={r.id} className="list-item" data-testid="rental-item">
                <span className="grow">
                  <strong>{l ? pickLocalized(l.title, locale) : r.listingId}</strong>
                  <span className="small muted my-listing__meta">
                    {projects.find((p) => p.id === r.projectId)?.name ?? r.projectId}
                    <span className="sep" aria-hidden="true" />
                    {r.endsAt
                      ? t("market.rental_until", { time: formatDateTime(r.endsAt, locale, tz, { dateStyle: "medium" }) })
                      : t("market.rental_since", { time: formatDateTime(r.startsAt, locale, tz, { dateStyle: "medium" }) })}
                    <span className="sep" aria-hidden="true" />
                    {formatMicroUsd(r.priceMicroUsd, locale)}
                  </span>
                </span>
                <Badge tone={active ? "success" : "neutral"}>{active ? t("market.rental_active") : t("market.rental_ended")}</Badge>
                <Link to={`/p/${r.projectId}/a/${r.agentId}`} className="ui-btn ui-btn--secondary ui-btn--sm" data-testid="rental-open">
                  {t("market.open_agent")}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="users" title={t("market.rentals_empty")}>
          <Link to={`/t/${tid}/market`} className="ui-btn ui-btn--secondary ui-btn--md">
            {t("market.nav.browse")}
          </Link>
        </EmptyState>
      )}
    </div>
  );
}
